import { collectContext, collectPageParams } from "./context.js";
import { resolveDynamicSlug } from "./dynamic-campaign-slug.js";

import type { FormVueInlineOptions, FormVueModalOptions, FormVueContactOptions, FormVueContentOptions, FormVueVisualizerOptions } from "./schema-types.js";
import { SIZE_PRESETS, DEFAULT_COLORS, DEFAULTS } from "./defaults.js";

import type { BaseConfig, InlineConfig, ModalConfig, ContactConfig, ContentConfig, VisualizerConfig, TriggerConfig, ResolvedAttribution } from "./types.js";

declare const __FORMVUE_API_URL__: string;
declare const __FORMVUE_SHARE_URL__: string;
const API_URL = __FORMVUE_API_URL__;
const SHARE_URL = __FORMVUE_SHARE_URL__;

const LOCAL_API_URL = "http://localhost:5001";
const LOCAL_SHARE_URL = "http://localhost:3000";

type BaseOptions = FormVueInlineOptions | FormVueModalOptions | FormVueContactOptions | FormVueContentOptions | FormVueVisualizerOptions;

/**
 * Resolves the API origins. With `dev` off, the baked beta/prod URLs are used.
 * `dev: true` targets the local web proxy (share) + local backend (logo);
 * a string overrides both with a custom origin. Proxy paths are unchanged.
 */
function resolveOrigins(dev: boolean | string | undefined): { apiUrl: string; shareUrl: string } {
  if (!dev) return { apiUrl: API_URL, shareUrl: SHARE_URL };
  if (typeof dev === "string") return { apiUrl: dev, shareUrl: dev };
  return { apiUrl: LOCAL_API_URL, shareUrl: LOCAL_SHARE_URL };
}

function resolveAttribution(options: BaseOptions): ResolvedAttribution {
  const defaults = { source: "embed-button", medium: "embed-button" };
  const passthrough = options.attribution?.passQueryParams !== false;
  const callRailEnabled = options.attribution?.callrail?.enabled === true;

  let pageUtm: Record<string, string> = {};
  let metadata: Record<string, string> = {};

  if (passthrough) {
    const page = collectPageParams(callRailEnabled ? ["callrail"] : []);
    pageUtm = page.utm;
    metadata = page.metadata;
  }

  // Host-supplied metadata wins on collision: the integrator naming a key in
  // code is a stronger statement of intent than whatever rode in on the query
  // string. Integrations that pass none are unaffected — this only adds keys.
  // Note `passQueryParams: false` suppresses the URL half, never this half.
  metadata = { ...metadata, ...options.attribution?.metadata };

  const staticUtm = options.attribution?.utm;

  const rawSlug = options.attribution?.campaignSlug;
  const campaignSlug =
    rawSlug === "auto" ? resolveDynamicSlug(window.location.pathname) : rawSlug;

  return {
    campaignId: options.attribution?.campaignId,
    campaignSlug,
    callrail: callRailEnabled ? { enabled: true } : undefined,
    utm: {
      source: staticUtm?.source ?? pageUtm.source ?? defaults.source,
      medium: staticUtm?.medium ?? pageUtm.medium ?? defaults.medium,
      campaign: staticUtm?.campaign ?? pageUtm.campaign,
      content: staticUtm?.content ?? pageUtm.content,
      term: staticUtm?.term ?? pageUtm.term,
    },
    metadata,
  };
}

/**
 * Metadata keys that describe the *browser that rendered the embed* rather than
 * the lead, and so must not travel on the share URL — that link is opened on a
 * different device when it arrives by SMS.
 *
 * `fbp` is Meta's first-party browser cookie. Presenting the original browser's
 * fbp from the recipient's phone is wrong-device data: it degrades match
 * quality rather than helping, and it has no continuity value. `fbc` is not
 * here on purpose — it is click-bound, not device-bound, so carrying it
 * preserves the ad-click attribution the phone-side completion would otherwise
 * lose. `meta_event_id` likewise stays, because the SMS path continues the same
 * logical lead and sharing the id is exactly what makes Meta count it once.
 */
const DEVICE_BOUND_METADATA_KEYS = new Set(["fbp"]);

function buildShareUrl(
  shareOrigin: string,
  formId: string,
  attribution: ResolvedAttribution,
  prefill: Record<string, string>,
): string {
  const base = `${shareOrigin}/${formId}`;
  const params = new URLSearchParams();

  const { utm, metadata } = attribution;
  if (utm.source) params.set("utm_source", utm.source);
  if (utm.medium) params.set("utm_medium", utm.medium);
  if (utm.campaign) params.set("utm_campaign", utm.campaign);
  if (utm.content) params.set("utm_content", utm.content);
  if (utm.term) params.set("utm_term", utm.term);

  for (const [key, value] of Object.entries(metadata)) {
    if (DEVICE_BOUND_METADATA_KEYS.has(key)) continue;
    params.set(key, value);
  }

  // The mobile link opens the form with no submission. The URL is the only
  // path for the prefill there.
  for (const [key, value] of Object.entries(prefill)) {
    params.set(key, value);
  }

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function resolveBase(options: BaseOptions): BaseConfig {
  const colors = { ...DEFAULT_COLORS, ...("colors" in options ? options.colors : {}) };
  const logoFill = colors.logoIcon.replace("#", "");
  const logoParams = new URLSearchParams({ formId: options.formId, fill: logoFill }).toString();
  const attribution = resolveAttribution(options);
  const { apiUrl, shareUrl: shareOrigin } = resolveOrigins(options.dev);
  const prefill = { ...options.prefill };

  return {
    formId: options.formId,
    embedApiUrl: shareOrigin,
    shareUrl: buildShareUrl(shareOrigin, options.formId, attribution, prefill),
    logoUrl: `${apiUrl}/logo.svg?${logoParams}`,
    locale: options.locale ?? DEFAULTS.locale,
    embed: collectContext(options._platform ?? DEFAULTS.platform),
    attribution,
    prefill,
    mobileNewTab: options.mobileNewTab,
    _formConfig: options._formConfig,
    preview: options.preview,
    onSubmit: options.onSubmit,
    onSuccess: options.onSuccess,
    onError: options.onError,
    onPreviewSubmit: options.onPreviewSubmit,
  };
}

export function resolveInlineConfig(options: FormVueInlineOptions): InlineConfig {
  const base = resolveBase(options);
  const colors = { ...DEFAULT_COLORS, ...options.colors };
  const sizePreset = SIZE_PRESETS[options.size ?? DEFAULTS.size];

  return {
    ...base,
    text: options.text ?? DEFAULTS.text,
    label: options.label ?? DEFAULTS.label,
    glass: options.glass ?? DEFAULTS.glass,
    width: options.width ?? DEFAULTS.width,
    height: options.height ?? sizePreset.height,
    fontSize: options.fontSize ?? sizePreset.fontSize,
    borderRadius: options.borderRadius ?? sizePreset.borderRadius,
    logoSize: sizePreset.logoSize,
    inputBorder: options.inputBorder ?? DEFAULTS.inputBorder,
    buttonBorder: options.buttonBorder ?? DEFAULTS.buttonBorder,
    variant: options.variant ?? DEFAULTS.variant,
    colors,
  };
}

function resolveTrigger(options: FormVueModalOptions | FormVueVisualizerOptions): TriggerConfig {
  const colors = { ...DEFAULT_COLORS, ...options.colors };
  const sizePreset = SIZE_PRESETS[options.size ?? DEFAULTS.size];

  return {
    text: options.text ?? DEFAULTS.text,
    label: options.label ?? DEFAULTS.label,
    glass: options.glass ?? DEFAULTS.glass,
    width: options.width ?? DEFAULTS.width,
    height: options.height ?? sizePreset.height,
    fontSize: options.fontSize ?? sizePreset.fontSize,
    borderRadius: options.borderRadius ?? sizePreset.borderRadius,
    logoSize: sizePreset.logoSize,
    buttonBorder: options.buttonBorder ?? DEFAULTS.buttonBorder,
    variant: options.variant ?? DEFAULTS.variant,
    colors,
  };
}

export function resolveModalConfig(options: FormVueModalOptions): ModalConfig {
  return {
    ...resolveBase(options),
    id: options.id ?? "",
    trigger: resolveTrigger(options),
  };
}

export function resolveVisualizerConfig(options: FormVueVisualizerOptions): VisualizerConfig {
  return {
    ...resolveBase(options),
    id: options.id ?? "",
    trigger: resolveTrigger(options),
    allowLocalOrigin: !!options.dev,
    fontFamily: options.fontFamily,
    displayFontFamily: options.displayFontFamily,
  };
}

export function resolveContactConfig(options: FormVueContactOptions): ContactConfig {
  const base = resolveBase(options);

  return {
    ...base,
    id: options.id ?? "",
    mobileRedirect: options.mobileRedirect ?? true,
  };
}

export function resolveContentConfig(options: FormVueContentOptions): ContentConfig {
  const base = resolveBase(options);

  return {
    ...base,
    id: options.id ?? "",
    mobileRedirect: options.mobileRedirect ?? true,
  };
}
